import { useMemo, useState } from "react"
import { Hand, Lock, LockOpen, Plus, Sparkles, Undo2 } from "lucide-react"
import type { AppShell } from "@/App"
import { confirmDay, ensureManualPlan, errorMessage, proposePlan, undoEdit, type Order, type PlanRoute, type PlanView, type Vehicle, type Warehouse } from "@/lib/api"
import { day10, dayShort, eur, hhmm, kg, m3, n } from "@/lib/format"
import { AsyncButton, Note, Panel, Tag, Td, Th } from "@/components/kit"
import { AssignDialog } from "@/pages/AssignDialog"
import { isOpen } from "@/pages/Orders"
import { RoutePanel } from "@/pages/RoutePanel"
import { cn } from "@/lib/utils"

const toMin = (hm: string | null | undefined, fallback: number) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm ?? "")
  return m ? Number(m[1]) * 60 + Number(m[2]) : fallback
}

function Bar({ value, max, label, title }: { value: number; max: number; label: string; title: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  const over = max > 0 && value > max
  return (
    <div className="flex items-center gap-1.5" title={title}>
      <span className="w-6 text-[10px] text-muted-foreground">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
        <div className={cn("h-full rounded-full", over ? "bg-red-500" : pct > 90 ? "bg-amber-500" : "bg-accent")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function RouteCell({ route, vehicle, selected, onSelect }: { route: PlanRoute; vehicle: Vehicle; selected: boolean; onSelect: () => void }) {
  const start = route.start_min ?? toMin(vehicle.shift_start, 480)
  const shiftEnd = toMin(vehicle.shift_end, 1080)
  const overtime = (route.end_min ?? 0) > shiftEnd
  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex w-full flex-col gap-1 rounded-lg border bg-card p-2 text-left transition-colors hover:border-foreground/30",
        selected && "border-accent ring-1 ring-accent",
        route.status === "confirmed" && "bg-green-50",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <b className="text-[13px]">{route.stop_count} paragens</b>
        <span className="font-mono text-[11.5px]">{eur(route.cost_variable)}</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {n(route.km)?.toLocaleString("pt-PT")} km · volta {hhmm(route.end_min)}
        {overtime && <span className="text-red-700"> · hora extra</span>}
      </div>
      <Bar label="kg" value={n(route.kg) ?? 0} max={n(vehicle.max_kg) ?? 0} title={`${kg(route.kg)} de ${kg(vehicle.max_kg)}`} />
      <Bar label="m³" value={n(route.m3) ?? 0} max={n(vehicle.max_m3) ?? 0} title={`${m3(route.m3)} de ${m3(vehicle.max_m3)}`} />
      <Bar label="h" value={(route.end_min ?? start) - start} max={shiftEnd - start} title={`${hhmm(start)} → ${hhmm(route.end_min)} (turno até ${vehicle.shift_end})`} />
      {route.status === "confirmed" && <Tag tone="ok">confirmada</Tag>}
      {route.problems?.length > 0 && <Tag tone="bad" title={route.problems.join("\n")}>{route.problems.length} problema(s)</Tag>}
    </button>
  )
}

/**
 * Proposta da frota (F1): gerar, KPIs com o custo variável e o fixo à parte
 * (D9), diff face à anterior (D10), quadro dias × veículos e a rota aberta com
 * as paragens. Mover, fixar, mapa e documentos chegam a seguir.
 */
export function FleetBoard({
  shell,
  view,
  vehicles,
  warehouse,
  orders,
  onChanged,
  onReload,
}: {
  shell: AppShell
  view: PlanView
  vehicles: Vehicle[]
  warehouse: Warehouse | null
  orders: Order[]
  onChanged: (v: PlanView) => void
  /** Volta a ler a proposta depois de uma edição. */
  onReload: () => Promise<void>
}) {
  const [selected, setSelected] = useState<string | null>(null)
  // Célula livre (veículo × dia) → atribuir encomendas a essa rota à mão.
  const [assignTo, setAssignTo] = useState<{ vehicle_id: string; day: string } | null>(null)
  const canEdit = shell.role >= 5
  const plan = view.plan
  const manual = plan?.stats?.mode === "manual"
  // Colunas: os dias da proposta mais hoje e o horizonte (atribuíveis à mão).
  const days = useMemo(() => [...new Set([...(plan?.days ?? []).map((d) => day10(d)), ...(view.assignable_days ?? [])])].filter((d): d is string => Boolean(d)).sort(), [plan?.days, view.assignable_days])
  const byOrder = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders])
  // Por atribuir à frota: em aberto, com localização, fora de qualquer rota (as da transportadora também podem vir para a frota).
  const inRoute = useMemo(() => new Set(view.stops.filter((s) => s.result !== "failed").map((s) => s.order_id)), [view.stops])
  const assignable = useMemo(() => orders.filter((o) => isOpen(o) && o.block_reason !== "geo" && !inRoute.has(o.id)), [orders, inRoute])
  const startManual = async () => {
    try {
      onChanged(await ensureManualPlan(shell))
      shell.toast("success", "Planeamento à mão", "Carrega numa célula livre para atribuir encomendas a esse veículo e dia.")
    } catch (err) {
      shell.toast("error", "Não foi possível começar", errorMessage(err))
    }
  }
  const activeVehicles = vehicles.filter((v) => v.active !== false)
  const route = view.routes.find((r) => r.id === selected) ?? null

  const generate = async () => {
    try {
      const next = await proposePlan(shell)
      onChanged(next)
      setSelected(null)
      shell.toast("success", "Proposta gerada", `${next.stops.length} encomendas na frota · ${next.plan?.carrier.length ?? 0} por transportadora`)
    } catch (err) {
      shell.toast("error", "Não foi possível gerar a proposta", errorMessage(err))
    }
  }

  const run = async (title: string, fn: () => Promise<unknown>) => {
    try {
      await fn()
      await onReload()
      shell.toast("success", title)
    } catch (err) {
      shell.toast("error", title, errorMessage(err))
    }
  }
  const undo = () =>
    run("Edição desfeita", async () => {
      const r = await undoEdit(shell)
      return r.summary
    })

  const t = plan?.totals
  const saving = t ? t.all_carrier - (t.fleet_variable + t.carrier) : 0

  return (
    <Panel
      title="Frota própria"
      subtitle={plan ? (manual ? `Planeamento à mão · ${view.stops.length} na frota · ${plan.carrier.length} por transportadora` : `Proposta de ${plan.created_at ? new Date(plan.created_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "agora"} · ${plan.order_count} encomendas`) : "Quadro dias × veículos."}
      actions={
        canEdit ? (
          <div className="flex gap-2">
            {plan && (
              <AsyncButton size="sm" variant="outline" disabled={!view.undo_count} onRun={undo} title="Desfaz a última edição à mão">
                <Undo2 className="h-3.5 w-3.5" /> Desfazer{view.undo_count ? ` (${view.undo_count})` : ""}
              </AsyncButton>
            )}
            {!plan && (
              <AsyncButton size="sm" variant="outline" onRun={startManual} title="Quadro vazio: atribuis tu cada encomenda a um veículo e dia, ou a uma transportadora">
                <Hand className="h-3.5 w-3.5" /> Planear à mão
              </AsyncButton>
            )}
            <AsyncButton size="sm" onRun={generate} title={manual ? "Otimiza o resto: o que atribuíste à mão fica onde está (fixado)" : "Re-otimiza: respeita as rotas confirmadas e as encomendas fixadas"}>
              <Sparkles className="h-3.5 w-3.5" /> {plan ? (manual ? "Otimizar o resto" : "Re-otimizar") : "Gerar proposta"}
            </AsyncButton>
          </div>
        ) : undefined
      }
    >
      {!plan ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          Ainda não há plano. «Planear à mão» abre o quadro vazio: carregas numa célula (veículo × dia) e escolhes as encomendas, ou atribuis a partir das Encomendas. «Gerar proposta» decide sozinha, encomenda a encomenda, entre a frota e a transportadora mais barata no prazo, e faz as rotas (uns 15 segundos).
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-muted-foreground">
            <span><b className="font-mono text-foreground">{view.stops.length}</b> na frota · <b className="font-mono text-foreground">{plan.carrier.length}</b> por transportadora{plan.unassigned.length ? <> · <b className="font-mono text-red-700">{plan.unassigned.length}</b> sem opção</> : null}</span>
            <span title="km × €/km + portagens + hora extra">Custo variável da frota: <b className="font-mono text-foreground">{eur(t?.fleet_variable)}</b></span>
            <span>Transportadoras: <b className="font-mono text-foreground">{eur(t?.carrier)}</b></span>
            <span title="O que custaria mandar tudo pela transportadora mais barata no prazo">Tudo por transportadora: <b className="font-mono text-foreground">{eur(t?.all_carrier)}</b></span>
            <span title="Tudo por transportadora − (custo variável da frota + transportadoras). Não inclui o custo fixo, que se paga de qualquer forma.">
              Poupança: <b className={cn("font-mono", saving >= 0 ? "text-green-700" : "text-red-700")}>{eur(saving)}</b>
            </span>
            {!manual && <span title="Salário, seguro e amortização dos dias em que o veículo sai; não entra no custo por encomenda (D9)">Custo fixo da frota ({plan.days.length} dias): <b className="font-mono text-foreground">{eur(t?.fleet_fixed)}</b></span>}
            <span><b className="font-mono text-foreground">{assignable.length}</b> por atribuir à frota</span>
          </div>
          {plan.unassigned.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-[12.5px] text-red-800">
              Sem transportadora no prazo e sem lugar na frota: {plan.unassigned.map((id) => byOrder.get(id)?.source_ref ?? id.slice(0, 8)).join(", ")}. Fixa-as num veículo e dia, ou alarga o prazo.
            </div>
          )}
          {plan.diff && (
            <Note>
              Face à proposta anterior: {plan.diff.new_orders} novas, {plan.diff.changed_day} mudaram de dia, {plan.diff.changed_vehicle} de veículo, {plan.diff.to_carrier} passaram para transportadora e {plan.diff.to_fleet} para a frota.
            </Note>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed border-collapse">
              <thead>
                <tr>
                  <Th className="w-36">Veículo</Th>
                  {days.map((d) => {
                    const dayRoutes = view.routes.filter((r) => day10(r.day) === d)
                    const confirmed = dayRoutes.length > 0 && dayRoutes.every((r) => r.status === "confirmed")
                    const problems = dayRoutes.some((r) => r.problems?.length)
                    return (
                      <Th key={d}>
                        <div className="flex items-center justify-between gap-1">
                          <span>{dayShort(d)}</span>
                          {canEdit && dayRoutes.length > 0 && (
                            <AsyncButton
                              size="sm"
                              variant="ghost"
                              className="h-6 px-1.5 text-[11px]"
                              disabled={!confirmed && problems}
                              title={!confirmed && problems ? "Há rotas com problemas: resolve antes de confirmar" : undefined}
                              onRun={() => run(confirmed ? `${dayShort(d)} reaberto` : `${dayShort(d)} confirmado`, () => confirmDay(shell, d, confirmed))}
                            >
                              {confirmed ? <><LockOpen className="h-3 w-3" /> Reabrir</> : <><Lock className="h-3 w-3" /> Confirmar</>}
                            </AsyncButton>
                          )}
                        </div>
                      </Th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {activeVehicles.map((v) => (
                  <tr key={v.id}>
                    <Td className="align-top">
                      <b className="text-[13px]">{v.name}</b>
                      <div className="text-[11px] text-muted-foreground">{kg(v.max_kg)} · {m3(v.max_m3)}{v.has_tail_lift ? " · plataforma" : ""}</div>
                    </Td>
                    {days.map((d) => {
                      const r = view.routes.find((x) => x.vehicle_id === v.id && day10(x.day) === d)
                      return (
                        <Td key={d} className="align-top">
                          {r ? (
                            <>
                              <RouteCell route={r} vehicle={v} selected={r.id === selected} onSelect={() => setSelected(r.id === selected ? null : r.id)} />
                              {canEdit && r.status !== "confirmed" && (
                                <button className="mt-1 flex w-full items-center justify-center gap-1 rounded-md border border-dashed py-0.5 text-[11px] text-muted-foreground hover:border-foreground/30 hover:text-foreground" onClick={() => setAssignTo({ vehicle_id: v.id, day: d })}>
                                  <Plus className="h-3 w-3" /> encomendas
                                </button>
                              )}
                            </>
                          ) : canEdit ? (
                            <button className="w-full rounded-lg border border-dashed p-2 text-center text-[11px] text-muted-foreground hover:border-foreground/30 hover:text-foreground" title="Atribuir encomendas a este veículo neste dia" onClick={() => setAssignTo({ vehicle_id: v.id, day: d })}>
                              livre · <Plus className="inline h-3 w-3" /> atribuir
                            </button>
                          ) : (
                            <div className="rounded-lg border border-dashed p-2 text-center text-[11px] text-muted-foreground">não sai</div>
                          )}
                        </Td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {route && (
            <RoutePanel shell={shell} view={view} route={route} vehicles={vehicles} warehouse={warehouse} orders={byOrder} canEdit={canEdit} onReload={onReload} />
          )}
        </div>
      )}
      {assignTo && plan && (
        <AssignDialog
          shell={shell}
          view={view}
          vehicles={vehicles}
          candidates={assignable}
          presetTarget={assignTo}
          onClose={() => setAssignTo(null)}
          onApplied={onReload}
        />
      )}
    </Panel>
  )
}
